import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Users, Shield, User, Briefcase, Mail, Search, Trash2, CheckCircle2, 
  AlertCircle, RefreshCcw, Eye, EyeOff, Save, Info, Lock, Settings, 
  Globe, Smartphone, ChevronRight, X, List, LayoutGrid, Award, Key, MapPin, Calendar
} from 'lucide-react';
import { pb } from '../../lib/pb';
import '../../styles/admin.css';

const ROLE_CONFIG = {
  'admin': { icon: <Shield size={16} />, label: 'Administrateur', color: '#6366f1', bg: '#eef2ff' },
  'commercial': { icon: <Briefcase size={16} />, label: 'Commercial', color: '#f59e0b', bg: '#fffbeb' },
  'worker': { icon: <Smartphone size={16} />, label: 'Salarié / Chantier', color: '#10b981', bg: '#f0fdf4' },
  'user': { icon: <User size={16} />, label: 'Client / Visiteur', color: '#94a3b8', bg: '#f8fafc' }
};

const PRESET_COLORS = [
  { name: 'Bleu', value: '#3B82F6' },
  { name: 'Émeraude', value: '#10B981' },
  { name: 'Ambre', value: '#F59E0B' },
  { name: 'Rose', value: '#F43F5E' },
  { name: 'Violet', value: '#8B5CF6' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Orange', value: '#FB923C' },
  { name: 'Ardoise', value: '#64748B' },
  { name: 'Fuchsia', value: '#D946EF' },
  { name: 'Lime', value: '#84CC16' },
];

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState('');
  const [fixing, setFixing] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [success, setSuccess] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await pb.collection('users').getList(1, 200, {
        filter: search ? `email ~ "${search}" || name ~ "${search}" || username ~ "${search}"` : '',
        sort: '-created'
      });
      setItems(res.items);
    } catch (err) {
      setError("Accès restreint. Vérifiez les droits 'List' dans PocketBase.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search]);

  useEffect(() => {
    let unsub = null;
    (async () => {
      try {
        unsub = await pb.collection('users').subscribe('*', () => load());
      } catch (e) { console.error(e); }
    })();
    return () => { if (unsub) pb.collection('users').unsubscribe('*'); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleVerified = async (u) => {
    const oldStatus = u.verified;
    setItems(prev => prev.map(item => item.id === u.id ? { ...item, verified: !oldStatus } : item));

    try {
      await pb.collection('users').update(u.id, { verified: !oldStatus });
      setSuccess(!oldStatus ? 'Compte vérifié ✅' : 'Vérification retirée 🛠️');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      setItems(prev => prev.map(item => item.id === u.id ? { ...item, verified: oldStatus } : item));
      setError("Erreur de mise à jour");
    }
  };

  const handleFixVisibility = async () => {
    if (!window.confirm("Forcer la visibilité de tous les emails pour l'administration ?")) return;
    setFixing(true);
    try {
      let count = 0;
      for (const u of items) {
        if (!u.emailVisibility) {
          try {
            await pb.collection('users').update(u.id, { emailVisibility: true });
            count++;
          } catch (e) { /* silent */ }
        }
      }
      setSuccess(`${count} comptes sécurisés ✨`);
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (err) { setError("Erreur lors de la maintenance massive."); } 
    finally { setFixing(false); }
  };

  const handleRoleChange = async (u, newRole) => {
    const oldRole = u.role;
    setItems(prev => prev.map(item => item.id === u.id ? { ...item, role: newRole } : item));
    
    try {
      await pb.collection('users').update(u.id, { role: newRole });
      setSuccess(`Rôle mis à jour : ${ROLE_CONFIG[newRole].label} ✨`);
      setTimeout(() => setSuccess(''), 2500);
    } catch (e) {
      setItems(prev => prev.map(item => item.id === u.id ? { ...item, role: oldRole } : item));
      setError("Impossible de modifier le rôle. Vérifiez vos privilèges PocketBase.");
    }
  };

  const handleDelete = async (u) => {
    if(!window.confirm(`Supprimer définitivement l'utilisateur ${u.email} ?`)) return;
    try {
      await pb.collection('users').delete(u.id);
      setSuccess('Compte supprimé 🗑️');
      setTimeout(() => setSuccess(''), 2000);
      load();
    } catch (e) { setError("Impossible de supprimer cet utilisateur."); }
  };

  return (
    <div className="admin-main animation-fade-in">
      <header className="admin-header-premium">
        <div className="header-content">
          <div className="header-tag">Sécurité & Accès</div>
          <h1>Supervision des Comptes <span className="count-badge">{items.length}</span></h1>
          <p className="header-subtitle">Gérez les privilèges de connexion et assurez la visibilité des données critiques.</p>
        </div>
        <button className="btn-add-premium" onClick={handleFixVisibility} disabled={fixing}>
          {fixing ? <RefreshCcw size={20} className="spin"/> : <Shield size={20} />}
          <span>{fixing ? 'Maintenance...' : 'Réparer Visibilité'}</span>
        </button>
      </header>

      {success && <div className="toast-success"><CheckCircle2 size={18}/> {success}</div>}
      {error && <div className="toast-error"><AlertCircle size={18}/> {error}</div>}

      <div className="admin-controls-bar">
        <div className="search-box-premium">
          <Search size={18} className="search-icon" />
          <input 
            placeholder="Rechercher par nom, email ou identifiant..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
          />
        </div>

        <div className="filters-and-view">
          <button className="btn-refresh-premium" onClick={load} title="Actualiser la liste">
            <RefreshCcw size={18} className={loading ? 'spin' : ''} />
          </button>
          <div className="view-switcher-premium">
            <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}><List size={18}/></button>
            <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><LayoutGrid size={18}/></button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <RefreshCcw className="spin" size={32} />
          <p>Chargement de la base utilisateurs...</p>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="empty-state-card">
          <Users size={48} />
          <h3>Aucun utilisateur trouvé</h3>
          <p>Affinez vos critères de recherche ou vérifiez vos permissions PocketBase.</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className={`users-content-container ${viewMode}`}>
          {viewMode === 'list' ? (
            <div className="premium-list">
              {items.map((u) => {
                const conf = ROLE_CONFIG[u.role] || ROLE_CONFIG.user;
                return (
                  <div key={u.id} className="list-item-row no-drag">
                    <div className="item-icon-box rounded" style={{ background: conf.bg }}>
                       <span style={{ color: conf.color, fontWeight: 900, fontSize: 16 }}>
                          {u.name ? u.name.charAt(0).toUpperCase() : u.username.charAt(0).toUpperCase()}
                       </span>
                    </div>
                    <div className="item-info">
                      <div className="item-title">{u.name || u.username}</div>
                      <div className="item-meta">
                        {u.email ? u.email : <span className="masked-label">Email Masqué</span>} • ID: {u.username}
                      </div>
                    </div>
                    <div className="item-status">
                       <select 
                         className="role-select-premium" 
                         value={u.role || 'user'} 
                         onChange={(e) => handleRoleChange(u, e.target.value)}
                         style={{ color: conf.color, background: conf.bg }}
                       >
                         {Object.entries(ROLE_CONFIG).map(([key, cfg]) => (
                           <option key={key} value={key}>{cfg.label}</option>
                         ))}
                       </select>
                    </div>
                    <div className="item-actions">
                      <button className={`action-btn ${u.verified ? 'ok' : 'warn'}`} title={u.verified ? 'Vérifié' : 'Non vérifié'} onClick={() => handleToggleVerified(u)}>
                         {u.verified ? <CheckCircle2 size={18}/> : <AlertCircle size={18}/>}
                      </button>
                      <button className="action-btn delete" onClick={() => handleDelete(u)} title="Supprimer">
                        <Trash2 size={18}/>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="premium-grid-users">
              {items.map((u) => {
                const conf = ROLE_CONFIG[u.role] || ROLE_CONFIG.user;
                return (
                  <div key={u.id} className="user-card-premium animation-slide-up">
                    <div className="card-security-header">
                       <button 
                         className={`status-dot-btn ${u.verified ? 'active' : ''}`} 
                         title={u.verified ? 'Vérifié' : 'En attente de vérification'}
                         onClick={() => handleToggleVerified(u)}
                       ></button>
                       <div className="role-badge" style={{ color: conf.color }}>{conf.icon}</div>
                    </div>
                    
                    <div className="user-passport-avatar" style={{ background: conf.bg }}>
                       <div className="initials" style={{ color: conf.color }}>
                          {u.name ? u.name.charAt(0).toUpperCase() : u.username.charAt(0).toUpperCase()}
                       </div>
                       {u.verified && <div className="shield-check"><Award size={12}/></div>}
                    </div>

                    <div className="user-passport-body">
                       <h3 className="user-fullname">{u.name || u.username}</h3>
                       <select 
                         className="role-select-premium minimal" 
                         value={u.role || 'user'} 
                         onChange={(e) => handleRoleChange(u, e.target.value)}
                       >
                         {Object.entries(ROLE_CONFIG).map(([key, cfg]) => (
                           <option key={key} value={key}>{cfg.label}</option>
                         ))}
                       </select>
                       
                       <div className="user-email-box">
                          {u.email ? <span>{u.email}</span> : <span className="masked-text">Confidentialité active</span>}
                       </div>

                       <div className="user-passport-footer">
                           <code className="user-id">@{u.username}</code>
                           <div style={{ display: 'flex', gap: 6 }}>
                             <button className="btn-user-plan" onClick={() => setSelectedUser(u)} title="Gérer les chantiers"><Calendar size={16}/></button>
                             <button className="btn-user-delete" onClick={() => handleDelete(u)}><Trash2 size={16}/></button>
                           </div>
                        </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {selectedUser && <UserPlanningPanel user={selectedUser} onClose={() => setSelectedUser(null)} />}

      <style>{`
        .premium-grid-users { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 20px; padding: 20px 0; }
        
        .user-card-premium { background: #fff; border-radius: 28px; border: 1px solid var(--admin-border); padding: 25px; display: flex; flex-direction: column; align-items: center; text-align: center; transition: all 0.3s ease; position: relative; }
        .user-card-premium:hover { transform: translateY(-5px); border-color: var(--admin-primary); box-shadow: 0 15px 30px -10px rgba(0,0,0,0.06); }
        
        .btn-user-plan { border: none; background: #f0fdf4; color: #16a34a; cursor: pointer; border-radius: 8px; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .btn-user-plan:hover { background: #dcfce7; transform: scale(1.15); }

        .card-security-header { width: 100%; display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; }
        .status-dot-btn { width: 14px; height: 14px; border-radius: 50%; background: #cbd5e1; border: 2px solid #fff; box-shadow: 0 0 0 1px #e2e8f0; cursor: pointer; transition: all 0.2s; }
        .status-dot-btn.active { background: #10b981; box-shadow: 0 0 0 1px #10b981, 0 0 10px rgba(16, 185, 129, 0.4); }
        .status-dot-btn:hover { transform: scale(1.3); }
        .role-badge { opacity: 0.6; }

        .user-passport-avatar { width: 80px; height: 80px; border-radius: 24px; display: flex; align-items: center; justify-content: center; position: relative; margin-bottom: 15px; transition: transform 0.3s; }
        .user-card-premium:hover .user-passport-avatar { transform: scale(1.05) rotate(3deg); }
        .user-passport-avatar .initials { font-size: 32px; font-weight: 950; letter-spacing: -2px; }
        .shield-check { position: absolute; bottom: -6px; right: -6px; width: 24px; height: 24px; background: #0f172a; color: #fff; border-radius: 50%; border: 3px solid #fff; display: flex; align-items: center; justify-content: center; }

        .user-fullname { font-size: 17px; font-weight: 900; color: #0f172a; margin: 0 0 2px 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; width: 100%; }
        .user-role-label { font-size: 11px; font-weight: 800; text-transform: uppercase; color: #94a3b8; letter-spacing: 1px; margin-bottom: 15px; }
        
        .user-email-box { background: #f8fafc; padding: 8px 12px; border-radius: 12px; font-size: 12px; font-weight: 700; color: #64748b; margin-bottom: 20px; width: 100%; overflow: hidden; text-overflow: ellipsis; }
        .masked-text { color: #f43f5e; font-style: italic; opacity: 0.7; }

        .user-passport-footer { width: 100%; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f8fafc; padding-top: 15px; }
        .user-id { font-size: 10px; font-weight: 700; color: #cbd5e1; background: #f8fafc; padding: 2px 8px; border-radius: 6px; }
        .btn-user-delete { border: none; background: transparent; color: #cbd5e1; cursor: pointer; transition: all 0.2s; }
        .btn-user-delete:hover { color: #f43f5e; transform: scale(1.2); }

        .role-pill-signature { display: flex; align-items: center; gap: 8px; padding: 6px 14px; border-radius: 99px; font-size: 12px; font-weight: 800; }
        .role-select-premium { 
          border: none; padding: 6px 14px; border-radius: 99px; font-size: 12px; font-weight: 800; 
          cursor: pointer; appearance: none; outline: none; transition: all 0.2s;
          text-align: center;
        }
        .role-select-premium:hover { filter: brightness(0.95); transform: scale(1.05); }
        .role-select-premium.minimal { 
          background: transparent; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px;
          margin-bottom: 15px; font-size: 11px;
        }
        .role-select-premium.minimal:hover { color: var(--admin-primary); }

        .masked-label { color: #f43f5e; }
        
        .btn-refresh-premium { width: 40px; height: 40px; border-radius: 12px; border: 1px solid var(--admin-border); background: #fff; color: #64748b; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; }
        .btn-refresh-premium:hover { border-color: var(--admin-primary); color: var(--admin-primary); }

        .action-btn.ok { background: #ecfdf5; color: #10b981; }
        .action-btn.warn { background: #fffbeb; color: #f59e0b; }

        @media (max-width: 600px) {
          .premium-grid-users { grid-template-columns: 1fr; }
          .user-card-premium { flex-direction: row; text-align: left; padding: 15px; }
          .card-security-header { position: absolute; top: 15px; right: 15px; width: auto; flex-direction: column; gap: 8px; }
          .user-passport-avatar { width: 60px; height: 60px; margin-bottom: 0; margin-right: 15px; }
          .user-passport-body { flex: 1; flex-direction: column; align-items: flex-start; }
          .user-email-box { background: transparent; padding: 0; margin-bottom: 5px; }
          .user-passport-footer { border: none; padding: 0; }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// USER PLANNING PANEL — Slide-over drawer
// ============================================================
function UserPlanningPanel({ user, onClose }) {
  const [assignments, setAssignments] = useState([]);
  const [allChantiers, setAllChantiers] = useState([]);
  const [userData, setUserData] = useState(user);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ chantier: '', start_date: '', end_date: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const initials = (userData?.name || userData?.username || '?').slice(0, 2).toUpperCase();
  const conf = ROLE_CONFIG[userData?.role] || ROLE_CONFIG.user;

  const handleColorChange = async (newColor) => {
    try {
      const updated = await pb.collection('users').update(user.id, { color: newColor });
      setUserData(updated);
    } catch (e) { alert('Erreur lors du changement de couleur.'); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [as, ch] = await Promise.all([
        pb.collection('assignments').getFullList({ filter: `user = "${user.id}"`, expand: 'chantier', sort: '-start_date' }),
        pb.collection('chantiers').getFullList({ sort: 'title' })
      ]);
      setAssignments(as);
      setAllChantiers(ch);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!form.chantier || !form.start_date) return;
    setSaving(true);
    try {
      await pb.collection('assignments').create({
        user: user.id,
        chantier: form.chantier,
        start_date: new Date(form.start_date).toISOString(),
        end_date: form.end_date ? new Date(form.end_date).toISOString() : new Date(form.start_date).toISOString(),
        notes: form.notes
      });
      setForm({ chantier: '', start_date: '', end_date: '', notes: '' });
      setShowForm(false);
      load();
    } catch { alert('Erreur lors de l\'assignation.'); }
    finally { setSaving(false); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Retirer cette affectation ?')) return;
    try { await pb.collection('assignments').delete(id); load(); }
    catch { alert('Erreur lors de la suppression.'); }
  };

  const filtered = allChantiers.filter(c =>
    !search || c.title?.toLowerCase().includes(search.toLowerCase()) || c.client_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 30000, display: 'flex' }} onClick={(e) => e.target === e.currentTarget && onClose()}>
      {/* Backdrop */}
      <div style={{ flex: 1, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(4px)' }} onClick={onClose} />

      {/* Panel */}
      <div style={{ width: '100%', maxWidth: 460, background: '#fff', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 40px rgba(0,0,0,0.15)', overflowY: 'auto' }}>
        
        {/* Header */}
        <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 52, height: 52, borderRadius: 16, background: userData.color || conf.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 20, color: userData.color ? '#fff' : conf.color, flexShrink: 0 }}>{initials}</div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#0f172a' }}>{userData.name || userData.username}</h2>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{conf.label} · {userData.email}</div>
          </div>
          <button onClick={onClose} style={{ width: 36, height: 36, border: 'none', background: '#f8fafc', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
            <X size={18} />
          </button>
        </div>

        {/* User Color Selection */}
        <div style={{ padding: '0 24px 20px', borderBottom: '1px solid #f1f5f9' }}>
           <label style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Couleur de l'Intervenant</label>
           <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {PRESET_COLORS.map(c => (
                <button key={c.value} type="button" onClick={() => handleColorChange(c.value)}
                  style={{ 
                    width: 24, height: 24, borderRadius: '50%', border: userData.color === c.value ? '2px solid #fff' : 'none',
                    boxShadow: userData.color === c.value ? `0 0 0 2px ${c.value}` : 'none',
                    background: c.value, cursor: 'pointer', transition: 'all 0.2s', transform: userData.color === c.value ? 'scale(1.1)' : 'scale(1)'
                  }}
                  title={c.name}
                />
              ))}
           </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Action bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>Chantiers assignés ({assignments.length})</h3>
            <button onClick={() => setShowForm(v => !v)} style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 10, padding: '7px 14px', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>
              {showForm ? '✕ Annuler' : '+ Assigner un chantier'}
            </button>
          </div>

          {/* Assign form */}
          {showForm && (
            <form onSubmit={handleAssign} style={{ background: '#f8fafc', borderRadius: 16, padding: 16, border: '1px solid #e2e8f0', display: 'grid', gap: 12 }}>
              {/* Search chantier */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Rechercher un chantier</label>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Nom du chantier ou client..." style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 13, fontWeight: 600, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Chantier sélectionné *</label>
                <select required value={form.chantier} onChange={e => setForm({ ...form, chantier: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 13, fontWeight: 600 }}>
                  <option value="">Sélectionner...</option>
                  {filtered.map(c => <option key={c.id} value={c.id}>{c.title}{c.client_name ? ` — ${c.client_name}` : ''}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Du *</label>
                  <input type="datetime-local" required value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Au</label>
                  <input type="datetime-local" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12, boxSizing: 'border-box' }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Consignes</label>
                <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Instructions particulières..." style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 13, fontWeight: 600, boxSizing: 'border-box' }} />
              </div>
              <button type="submit" disabled={saving} style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 12, padding: '12px', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
                {saving ? 'Assignation...' : '✓ Confirmer l\'assignation'}
              </button>
            </form>
          )}

          {/* Assignments list */}
          {loading && <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Chargement...</div>}
          
          {!loading && assignments.length === 0 && !showForm && (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🏗️</div>
              <p style={{ margin: 0, fontWeight: 600, color: '#64748b' }}>Aucun chantier assigné</p>
              <p style={{ margin: '4px 0 0', fontSize: 13 }}>Utilisez "+ Assigner un chantier".</p>
            </div>
          )}

          {!loading && assignments.map(a => {
            const ch = a.expand?.chantier;
            return (
              <div key={a.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, flexShrink: 0, marginTop: 4, background: ch?.status === 'completed' ? '#22c55e' : ch?.status === 'active' ? '#3b82f6' : '#f59e0b' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>{ch?.title || '—'}</div>
                  {ch?.client_name && <div style={{ fontSize: 12, color: '#64748b' }}>{ch.client_name}</div>}
                  {ch?.location && <div style={{ fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={11}/>{ch.location}</div>}
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                    📅 {new Date(a.start_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                  {a.notes && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>💬 {a.notes}</div>}
                </div>
                <button onClick={() => handleRemove(a.id)} style={{ width: 30, height: 30, border: 'none', background: '#fff1f2', borderRadius: 8, color: '#f43f5e', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
